import { IfpApplicationState } from '@shared/sec-loans'
import { OpenModalEvent } from '@ui-common/components/Modal'
import { GlobalServicesContext } from '@ui-common/state'
import { formatFinnishCurrencyWithSymbol, getLanguage } from '@ui-common/utils'
import { ModalVisualMode } from '@ui-components/containers/ModalTypes'
import { IndentationText } from '@ui-components/decorative/IndentationText'
import { FlexColumnContainer } from '@ui-components/global/CommonStyling'
import { WizardPageSubHeaderProps } from '@ui-components/layout/WizardTypes'
import { AdditionalText, BodyText, LabelText } from '@ui-components/typography/Typography'
import { use } from 'react'
import { useIntl } from 'react-intl'

import { useIfpAnalyticsService } from '../analytics/analyticsContext'
import { useIfpFormContext } from '../steps/formContext'

export const useSubHeaderOptionsWithMenu = ({
  onGoToApplicationsClick,
  onNewApplicationClick,
}: {
  onGoToApplicationsClick: () => void
  onNewApplicationClick: () => void
}): WizardPageSubHeaderProps => {
  const { analyticsService } = useIfpAnalyticsService()
  const language = getLanguage()

  const {
    actionsNeeded,
    applicationState,
    isPrimaryApplicant,
    loan,
    user: { debtorUuid },
    onCancelApplication,
  } = useIfpFormContext()

  const { modalService } = use(GlobalServicesContext)
  const modalSend = modalService.send

  const { formatMessage } = useIntl()

  const loanDescription = `${loan.description[language]} (${formatFinnishCurrencyWithSymbol(loan.balance)})`

  const showCancelPromptModal = (): void => {
    const modalAnalyticsName = 'DeleteApplicationModal'
    const openCancelModalEvent: OpenModalEvent = {
      type: 'OPEN',
      data: {
        header: formatMessage({ id: 'delete-application-modal-header' }),
        children: (
          <>
            <BodyText>{formatMessage({ id: 'sec-loans-status-delete-loan-application-description' })}</BodyText>
            <IndentationText>
              <FlexColumnContainer>
                <LabelText>{formatMessage({ id: 'ifp-application-top-bar-heading' })}</LabelText>
                <AdditionalText>{loanDescription}</AdditionalText>
              </FlexColumnContainer>
            </IndentationText>
          </>
        ),
        primaryButtonOptions: {
          label: formatMessage({ id: 'common-cancel' }),
          onClick: () => {
            analyticsService.sendCustomEvent(modalAnalyticsName, 'CancelButton')
          },
        },
        secondaryButtonOptions: {
          label: formatMessage({ id: 'common-remove' }),
          onClick: () => {
            analyticsService.sendCustomEvent(modalAnalyticsName, 'ConfirmButton')
            onCancelApplication()
          },
        },
        mode: ModalVisualMode.Danger,
      },
    }

    analyticsService.sendModalViewEvent(modalAnalyticsName)
    modalSend(openCancelModalEvent)
  }

  let isCancellableState = false
  if (isPrimaryApplicant) {
    isCancellableState =
      !!applicationState &&
      [
        IfpApplicationState.Draft,
        IfpApplicationState.WaitingForCoDebtor,
        IfpApplicationState.ReturnedByCoDebtor,
      ].includes(applicationState)
  } else {
    isCancellableState =
      applicationState === IfpApplicationState.WaitingForCoDebtor &&
      !!debtorUuid &&
      !!actionsNeeded?.includes(debtorUuid)
  }

  return {
    title: formatMessage({ id: 'ifp-application-top-bar-heading' }),
    description: loanDescription,
    isFixedSubHeader: true,
    secondaryTopBarMenuProps: {
      menuAriaLabelOpen: formatMessage({ id: 'common-open-additional-features' }),
      menuAriaLabelClose: formatMessage({ id: 'common-close-additional-features' }),
      menuOptions: [
        {
          text: formatMessage({ id: 'loan-offer-status-step-menu-new-application-label' }),
          onClick: onNewApplicationClick,
        },
        {
          text: formatMessage({ id: 'loan-offer-status-step-menu-own-applications-label' }),
          onClick: onGoToApplicationsClick,
        },
      ].concat(
        isCancellableState
          ? [
              {
                text: formatMessage({ id: 'sec-loans-header-menu-delete-this-loan-application' }),
                onClick: showCancelPromptModal,
              },
            ]
          : [],
      ),
    },
  }
}
